(() => {
  if (globalThis.realSizeGuideInitialized) return;
  globalThis.realSizeGuideInitialized = true;

  const selectors = {
    back: '.real-size-guide__back',
    body: '.real-size-guide-modal__body',
    close: '.real-size-guide-modal__close, .real-size-guide-modal__overlay',
    closeButton: '.real-size-guide-modal__close',
    dialog: '.real-size-guide-modal__content',
    guide: '.real-size-guide',
    header: '.real-size-guide__header h3',
    introHeading: '.real-size-guide__intro h2',
    menu: '.real-size-guide__menu',
    modal: '.real-size-guide-modal',
    nav: '.real-size-guide__nav',
    panel: '.real-size-guide__panel',
    tableWrapper: '.table-responsive',
    trigger: '.real-size-guide-trigger',
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /** @type {HTMLElement | null} */
  let activeModal = null;
  /** @type {HTMLElement | null} */
  let activeTrigger = null;
  let resizeFrame = 0;

  /**
   * @param {HTMLElement} modal
   * @param {Element | null} heading
   */
  function labelDialog(modal, heading) {
    const dialog = modal.querySelector(selectors.dialog);
    if (!(dialog instanceof HTMLElement) || !(heading instanceof HTMLElement)) return;

    if (!heading.id) heading.id = `${modal.id}-${heading.matches('h2') ? 'title' : 'panel-title'}`;
    dialog.setAttribute('aria-labelledby', heading.id);
    dialog.removeAttribute('aria-label');
  }

  /**
   * @param {HTMLElement} modal
   */
  function prepareGuide(modal) {
    const guide = modal.querySelector(selectors.guide);
    if (!(guide instanceof HTMLElement)) return;

    labelDialog(modal, guide.querySelector(selectors.introHeading));

    for (const navButton of guide.querySelectorAll(selectors.nav)) {
      if (!(navButton instanceof HTMLElement)) continue;
      const target = navButton.dataset.sizeGuideTarget;
      if (target) navButton.setAttribute('aria-controls', target);
      navButton.setAttribute('aria-expanded', 'false');
    }

    for (const panel of guide.querySelectorAll(selectors.panel)) {
      if (!(panel instanceof HTMLElement)) continue;
      const heading = panel.querySelector(selectors.header);
      if (heading instanceof HTMLElement && !heading.id) heading.id = `${modal.id}-${panel.id}-title`;

      const accessibleName = heading?.textContent?.trim() || 'Size chart';
      for (const columnHeading of panel.querySelectorAll('thead th')) {
        if (!columnHeading.hasAttribute('scope')) {
          columnHeading.setAttribute('scope', 'col');
        }
      }

      for (const wrapper of panel.querySelectorAll(selectors.tableWrapper)) {
        if (!(wrapper instanceof HTMLElement)) continue;
        const tableSubheading = wrapper.previousElementSibling;
        const subheadingText = tableSubheading?.matches('h4, h5, h6') ? tableSubheading.textContent?.trim() : '';
        wrapper.dataset.sizeGuideRegionLabel = subheadingText
          ? `${accessibleName}, ${subheadingText}`
          : `${accessibleName} size chart`;
      }
    }
  }

  /**
   * Adds a keyboard stop and accessible region name only to tables that
   * actually overflow at the current viewport width.
   * @param {HTMLElement} modal
   */
  function updateScrollableTables(modal) {
    for (const wrapper of modal.querySelectorAll(selectors.tableWrapper)) {
      if (!(wrapper instanceof HTMLElement)) continue;

      const isVisible = wrapper.getClientRects().length > 0;
      const overflows = isVisible && wrapper.scrollWidth > wrapper.clientWidth + 1;

      if (overflows) {
        wrapper.dataset.sizeGuideOverflow = 'true';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('tabindex', '0');
        wrapper.setAttribute('aria-label', wrapper.dataset.sizeGuideRegionLabel || 'Size chart');
      } else if (wrapper.dataset.sizeGuideOverflow === 'true') {
        delete wrapper.dataset.sizeGuideOverflow;
        wrapper.removeAttribute('role');
        wrapper.removeAttribute('tabindex');
        wrapper.removeAttribute('aria-label');
      }
    }
  }

  /**
   * @param {HTMLElement} modal
   */
  function updateScrollableTablesAfterLayout(modal) {
    requestAnimationFrame(() => updateScrollableTables(modal));
  }

  /**
   * @param {HTMLElement} modal
   */
  function resetGuide(modal) {
    const guide = modal.querySelector(selectors.guide);
    if (!(guide instanceof HTMLElement)) return;

    const menu = guide.querySelector(selectors.menu);
    if (menu instanceof HTMLElement) menu.hidden = false;

    for (const navButton of guide.querySelectorAll(selectors.nav)) {
      navButton.setAttribute('aria-expanded', 'false');
    }

    for (const panel of guide.querySelectorAll(selectors.panel)) {
      if (panel instanceof HTMLElement) panel.hidden = true;
    }

    labelDialog(modal, guide.querySelector(selectors.introHeading));

    const body = modal.querySelector(selectors.body);
    if (body instanceof HTMLElement) body.scrollTop = 0;
    updateScrollableTablesAfterLayout(modal);
  }

  /**
   * @param {HTMLElement} modal
   * @param {boolean} [restoreFocus]
   */
  function closeModal(modal, restoreFocus = true) {
    const picker = modal.closest('variant-picker');
    const fallbackTrigger = picker?.querySelector(selectors.trigger);
    const trigger = activeModal === modal && activeTrigger?.isConnected ? activeTrigger : fallbackTrigger;

    modal.hidden = true;
    resetGuide(modal);
    trigger?.setAttribute('aria-expanded', 'false');

    if (activeModal === modal) {
      activeModal = null;
      activeTrigger = null;
    }

    if (!document.querySelector(`${selectors.modal}:not([hidden])`)) {
      document.body.classList.remove('real-size-guide-open');
    }

    if (restoreFocus && trigger instanceof HTMLElement) trigger.focus();
  }

  /**
   * @param {HTMLElement} trigger
   */
  function openModal(trigger) {
    const picker = trigger.closest('variant-picker');
    const modal = picker?.querySelector(selectors.modal);
    if (!(modal instanceof HTMLElement)) return;

    if (activeModal && activeModal !== modal) closeModal(activeModal, false);

    prepareGuide(modal);
    resetGuide(modal);
    modal.hidden = false;
    activeModal = modal;
    activeTrigger = trigger;
    document.body.classList.add('real-size-guide-open');
    trigger.setAttribute('aria-expanded', 'true');
    updateScrollableTablesAfterLayout(modal);

    const closeButton = modal.querySelector(selectors.closeButton);
    if (closeButton instanceof HTMLElement) closeButton.focus();
  }

  /**
   * @param {HTMLElement} button
   */
  function showPanel(button) {
    const guide = button.closest(selectors.guide);
    const target = button.dataset.sizeGuideTarget;
    if (!(guide instanceof HTMLElement) || !target) return;

    const panels = [...guide.querySelectorAll(selectors.panel)];
    const targetPanel = panels.find((panel) => panel instanceof HTMLElement && panel.id === target);
    if (!(targetPanel instanceof HTMLElement)) return;

    const menu = guide.querySelector(selectors.menu);
    if (menu instanceof HTMLElement) menu.hidden = true;

    for (const navButton of guide.querySelectorAll(selectors.nav)) {
      navButton.setAttribute('aria-expanded', navButton === button ? 'true' : 'false');
    }

    for (const panel of panels) {
      if (panel instanceof HTMLElement) panel.hidden = panel !== targetPanel;
    }

    const modal = guide.closest(selectors.modal);
    if (modal instanceof HTMLElement) {
      labelDialog(modal, targetPanel.querySelector(selectors.header));

      const body = modal.querySelector(selectors.body);
      if (body instanceof HTMLElement) body.scrollTop = 0;
      updateScrollableTablesAfterLayout(modal);
    }

    const backButton = targetPanel.querySelector(selectors.back);
    if (backButton instanceof HTMLElement) backButton.focus();
  }

  /**
   * @param {HTMLElement} modal
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(modal) {
    return [...modal.querySelectorAll(focusableSelector)].filter(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement} modal
   */
  function trapFocus(event, modal) {
    const focusableElements = getFocusableElements(modal);
    if (focusableElements.length === 0) {
      event.preventDefault();
      modal.querySelector(selectors.dialog)?.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === first || !modal.contains(activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (activeElement === last || !modal.contains(activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Guarantees horizontal keyboard scrolling for an overflowing size chart.
   * @param {KeyboardEvent} event
   */
  function handleTableArrowKey(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const wrapper = event.target.closest(selectors.tableWrapper);
    if (!(wrapper instanceof HTMLElement) || wrapper.dataset.sizeGuideOverflow !== 'true') return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    wrapper.scrollLeft += event.key === 'ArrowRight' ? 48 : -48;
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(selectors.trigger);
    if (trigger instanceof HTMLElement) {
      openModal(trigger);
      return;
    }

    const closeControl = event.target.closest(selectors.close);
    const closeControlModal = closeControl?.closest(selectors.modal);
    if (closeControlModal instanceof HTMLElement) {
      closeModal(closeControlModal);
      return;
    }

    const navButton = event.target.closest(selectors.nav);
    if (navButton instanceof HTMLElement) {
      showPanel(navButton);
      return;
    }

    const backButton = event.target.closest(selectors.back);
    const backButtonModal = backButton?.closest(selectors.modal);
    if (backButton instanceof HTMLElement && backButtonModal instanceof HTMLElement) {
      const activeNavButton = backButtonModal.querySelector(`${selectors.nav}[aria-expanded="true"]`);
      resetGuide(backButtonModal);

      const returnTarget =
        activeNavButton instanceof HTMLElement ? activeNavButton : backButtonModal.querySelector(selectors.nav);
      if (returnTarget instanceof HTMLElement) returnTarget.focus();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal || activeModal.hidden) return;

    handleTableArrowKey(event);

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(activeModal);
    } else if (event.key === 'Tab') {
      trapFocus(event, activeModal);
    }
  });

  window.addEventListener('resize', () => {
    if (!activeModal || activeModal.hidden) return;
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      if (activeModal) updateScrollableTables(activeModal);
    });
  });
})();
